import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import MobileDetect from 'mobile-detect';
import PerfectScrollbar from 'perfect-scrollbar';
import 'perfect-scrollbar/css/perfect-scrollbar.css';
import React, { createRef, useCallback, useEffect, useRef } from 'react';
import { connect } from 'app/modules/react-redux';
import withRouterAndRef from '../withRouterAndRef/withRouterAndRef';

type Props = {
	onScrollY?: (el: HTMLElement) => void;
	onScrollX?: (el: HTMLElement) => void;
	onScrollUp?: (el: HTMLElement) => void;
	onScrollDown?: (el: HTMLElement) => void;
	onScrollLeft?: (el: HTMLElement) => void;
	onScrollRight?: (el: HTMLElement) => void;
	onYReachStart?: (el: HTMLElement) => void;
	onYReachEnd?: (el: HTMLElement) => void;
	onXReachStart?: (el: HTMLElement) => void;
	onXReachEnd?: (el: HTMLElement) => void;
	scrollToTopOnRouteChange?: boolean;
	scrollToTopOnChildChange?: boolean;
	customScrollbars?: boolean;
	enable?: boolean;
	history: any;
	className?: string;
	children?: React.ReactNode;
	option: PerfectScrollbar.Options;
	id: string;
};

type Fuse = { settings: { current: { customScrollbars: boolean } } };

const md = new MobileDetect(window.navigator.userAgent);
const isMobile = md.mobile();

const handlerNameByEvent = {
	'ps-scroll-y': 'onScrollY',
	'ps-scroll-x': 'onScrollX',
	'ps-scroll-up': 'onScrollUp',
	'ps-scroll-down': 'onScrollDown',
	'ps-scroll-left': 'onScrollLeft',
	'ps-scroll-right': 'onScrollRight',
	'ps-y-reach-start': 'onYReachStart',
	'ps-y-reach-end': 'onYReachEnd',
	'ps-x-reach-start': 'onXReachStart',
	'ps-x-reach-end': 'onXReachEnd',
} as const;
Object.freeze(handlerNameByEvent);

const useStyles = makeStyles((theme) => ({
	root: {},
}));

const defaultProps = {
	enable: true,
	scrollToTopOnChildChange: false,
	scrollToTopOnRouteChange: false,
	option: {
		wheelPropagation: true,
	},
} as const;

const FuseScrollbars = React.forwardRef((props: Props, ref: any) => {
	const propsWithDefaults = {
		...defaultProps,
		...props,
	};

	ref = ref || createRef();
	const ps: any = useRef(null);
	const handlerByEvent = useRef(new Map());
	const classes = useStyles();
	const { customScrollbars } = propsWithDefaults;

	const handlerNameByEventCallback = (key: keyof typeof handlerNameByEvent) => {
		const eventHandler = handlerNameByEvent[key];
		const callback = propsWithDefaults[eventHandler];
		if (callback) {
			const handler = () => callback(ref.current);
			handlerByEvent.current.set(key, handler);
			ref.current.addEventListener(key, handler, false);
		}
	};

	const cbHandlerNameByEventCallback = useCallback(handlerNameByEventCallback, []);
	const hookUpEvents = useCallback(() => {
		Object.keys(handlerNameByEvent).forEach(cbHandlerNameByEventCallback as any);
	}, [cbHandlerNameByEventCallback]);

	const unHookUpEvents = useCallback(() => {
		handlerByEvent.current.forEach((value, key) => {
			if (ref.current) {
				ref.current.removeEventListener(key, value, false);
			}
		});
		handlerByEvent.current.clear();
	}, [ref]);

	const destroyPs = useCallback(() => {
		// console.info("destroy::ps");

		unHookUpEvents();

		if (!ps.current) {
			return;
		}
		ps.current.destroy();
		ps.current = null;
	}, [unHookUpEvents]);

	const createPs = useCallback(() => {
		// console.info("create::ps");

		if (isMobile || !ref || ps.current) {
			return;
		}

		ps.current = new PerfectScrollbar(ref.current, propsWithDefaults.option);

		hookUpEvents();
	}, [hookUpEvents, propsWithDefaults.option, ref]);

	useEffect(() => {
		function updatePs() {
			if (!ps.current) {
				return;
			}
			ps.current.update();
		}

		updatePs();
	});

	useEffect(() => {
		if (customScrollbars) {
			createPs();
		} else {
			destroyPs();
		}
	}, [createPs, customScrollbars, destroyPs]);

	const scrollToTop = useCallback(() => {
		if (ref?.current) {
			ref.current.scrollTop = 0;
		}
	}, [ref]);

	useEffect(() => {
		if (propsWithDefaults.scrollToTopOnChildChange) {
			scrollToTop();
		}
	}, [scrollToTop, propsWithDefaults.children, propsWithDefaults.scrollToTopOnChildChange]);

	useEffect(
		() =>
			propsWithDefaults.history.listen(() => {
				if (propsWithDefaults.scrollToTopOnRouteChange) {
					scrollToTop();
				}
			}),
		[scrollToTop, propsWithDefaults.history, propsWithDefaults.scrollToTopOnRouteChange],
	);

	useEffect(
		() => () => {
			destroyPs();
		},
		[destroyPs],
	);

	// console.info('render::ps');
	return (
		<div
			id={propsWithDefaults.id}
			className={clsx(classes.root, propsWithDefaults.className)}
			style={
				propsWithDefaults.customScrollbars && (propsWithDefaults.enable || true) && !isMobile
					? {
							position: 'relative',
							overflow: 'hidden',
						}
					: {}
			}
			ref={ref}
		>
			{propsWithDefaults.children}
		</div>
	);
});

function mapStateToProps({ fuse }: { fuse: Fuse }) {
	return {
		customScrollbars: fuse.settings.current.customScrollbars,
	};
}

export default connect(mapStateToProps, null, null, { forwardRef: true })(withRouterAndRef(FuseScrollbars)) as any;
